import React, { useState } from 'react';
import cls from 'classnames';
import { INFT } from '../../../types/nft';
import { SKHorizontalCardVariant1 } from '../HorizontalCardVariant1';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { SwapStepSvg } from '../SwapList/SwapStepSvg';
import { SwapStatus } from '../../../types/enums';
import { ButtonSizes, SKButton } from '../Button';
import { NoNFTsContainer } from '../NoNFTsContainer';
import { truncateAddress } from '../../../utils/truncateAddress';
import { ReactComponent as ArrowRight } from '../../../assets/svg/icons/arrow-right.svg';
import { ReactComponent as Refresh } from '../../../assets/svg/icons/refresh-cw-02.svg';
import { ReactComponent as Expand } from '../../../assets/svg/icons/expand-06.svg';
import { ReactComponent as Minimize } from '../../../assets/svg/icons/minimize-02.svg';
import { ReactComponent as Link } from '../../../assets/svg/icons/link-external-01.svg';
import { BadgeSizes, SKBadge } from '../Badge';
import { useWeb3React } from '@web3-react/core';
import { IconComp } from '../IconComponent';
import CopyToClipboard from 'react-copy-to-clipboard';
export interface ISKSwapCardProps {
  parts: {
    NFTs: INFT[];
    addedEther: number;
    walletAddress?: string;
  }[];
  txnHash?: string;
  className?: string;
  isUserProposer?: boolean;
  swapStatus?: SwapStatus;
  statusDates?: {
    statusSpan: string;
    timestamp: string;
  };
  onShowMoreClick?: () => void;
  displaySwapTxnDetails?: boolean;
  swapDate?: string;
  notExpandable?: boolean;
}
export const SKSwapCard: React.FC<ISKSwapCardProps> = ({
  parts,
  txnHash,
  className,
  swapStatus,
  statusDates,
  onShowMoreClick,
  displaySwapTxnDetails,
  swapDate,
  isUserProposer,
  notExpandable,
}) => {
  const { chainId } = useWeb3React();
  const [isActive, setIsActive] = useState<number>(0);
  const [activeNFTIndex, setActiveNFTIndex] = useState<number>(-1);
  const isBiggerThanMobile = useBreakpoint('48em');
  const isDesktop = useBreakpoint('75em');
  const showExpandToggle = swapStatus === 'EXECUTED' || swapStatus === 'CANCELLED' || swapStatus === 'REJECTED';
  const isTablet = useBreakpoint('58.75em');
  return (
    <div className="app-container mx-auto">
      <div
        className={cls(
          'flex flex-col gap-2 relative max-w-screen-2xl w-full mx-auto xs-only:px-0.5 transition-height duration-300 ease-in-out',
          isActive > 0 ? 'h-60' : 'flex sm:content-center sm:items-center sm:justify-between',
          className ? className : 'py-4',
        )}
      >
        <div className="flex xs-only:flex-col justify-center items-center h-full w-full gap-1">
          {parts[0].NFTs.length > 0 && (
            <SKHorizontalCardVariant1
              NFTs={parts[0].NFTs}
              addedEther={parts[0].addedEther}
              walletAddress={parts[0].walletAddress}
              className="md:w-1/2 w-full"
              onClickOnCard={(index: number): void => {
                if (isBiggerThanMobile && !notExpandable) {
                  setIsActive(1);
                  setActiveNFTIndex(index);
                }
              }}
              onClickRemoveActiveState={(): void => {
                setIsActive(0);
                setActiveNFTIndex(-1);
              }}
              isActive={isActive !== 0 ? true : false}
              isExpanded={isActive !== 0}
              activeNFTIndex={activeNFTIndex}
              notExpandable={notExpandable}
            />
          )}
          {parts[0].NFTs.length === 0 && (
            <NoNFTsContainer
              className="w-1/2 min-h-14.875 h-full flex flex-col justify-between"
              recipientAdress={parts[0].walletAddress}
              inOpenSwapView={true}
              innerContentClass="px-10"
            >
              <p>
                Waiting for <span className="font-semibold">you</span> to select your NFTs.
              </p>
            </NoNFTsContainer>
          )}
          <div className="my-auto h-full flex justify-center items-center content-center items-center">
            <SKBadge
              onlyIcon={true}
              iconRight={Refresh}
              color="black"
              size={BadgeSizes.SM}
              iconSize={40}
              className="cursor-default"
            />
          </div>
          {parts[1].NFTs.length > 0 && (
            <SKHorizontalCardVariant1
              NFTs={parts[1].NFTs}
              addedEther={parts[1].addedEther}
              walletAddress={parts[1].walletAddress}
              className="md:w-1/2 w-full"
              onClickOnCard={(index: number): void => {
                if (isBiggerThanMobile && !notExpandable) {
                  setIsActive(2);
                  setActiveNFTIndex(index);
                }
              }}
              onClickRemoveActiveState={(): void => {
                setIsActive(0);
                setActiveNFTIndex(-1);
              }}
              isActive={isActive !== 0 ? true : false}
              isExpanded={isActive !== 0}
              activeNFTIndex={activeNFTIndex}
              notExpandable={notExpandable}
            />
          )}
          {parts[1].NFTs.length === 0 && (
            <NoNFTsContainer
              className="w-1/2 min-h-14.875 h-full flex flex-col justify-between"
              recipientAdress={parts[1].walletAddress}
              inOpenSwapView={true}
              innerContentClass="px-10"
            >
              <p>
                Waiting for{' '}
                <span className="font-semibold">
                  {truncateAddress(parts[1].walletAddress?.toLowerCase() as string)}
                </span>{' '}
                to select the NFTs.
              </p>
            </NoNFTsContainer>
          )}
        </div>
        <div className="flex xs-only:flex-col items-center w-full text-tiny rounded-xl bg-white border border-cloud-900 transition-width duration-300 ease-in-out">
          {displaySwapTxnDetails && (
            <>
              <div className="md:min-h-5 xs-only:w-full sm-and-above:min-w-1/3 sm-and-above:w-1/3 md-and-above:min-w-1/4 md-and-above:w-25%  border-r xs-only:border-r-0 xs-only:border-b border-cloud-900 px-2 xs-only:py-1 flex flex-col justify-center">
                <p className="text-sm font-semibold xs-only:font-bold text-cloud-900 mb-0.5">Swap Date:</p>
                <h4 className="text-base text-cloud-900">{swapDate}</h4>
              </div>
              <div className="md:min-h-5 xs-only:w-full sm-and-above:min-w-1/3 sm-and-above:w-1/3 md-and-above:min-w-1/2 md-and-above:w-1/2 border-r xs-only:border-r-0 xs-only:border-b border-cloud-900 px-2 xs-only:py-1 flex flex-col justify-center">
                <p className="text-sm font-semibold text-cloud-900 mb-0.5">Txn Hash:</p>
                {txnHash && (
                  <CopyToClipboard text={txnHash}>
                    <p className="text-sm sm-and-above:text-base text-cloud-900 break-all">
                      {isTablet || !isBiggerThanMobile ? txnHash : truncateAddress(txnHash)}
                    </p>
                  </CopyToClipboard>
                )}
              </div>
              <div className="flex xs-only:flex-col xs-only:gap-y-1 justify-end xs-only:w-full sm-and-above:min-w-1/3 sm-and-above:w-1/3 md-and-above:min-w-1/4 md:w-25% p-2 gap-x-1.5">
                {isActive === 0 && isBiggerThanMobile && (
                  <SKButton
                    onClick={(): void => setIsActive(2)}
                    color="tertiary"
                    iconRight={isDesktop ? Expand : undefined}
                    size={isDesktop ? ButtonSizes.XL : ButtonSizes.SM}
                  >
                    <span className="text-base xs-only:text-xs text-cloud-900">Expand</span>
                  </SKButton>
                )}
                {isActive > 0 && isBiggerThanMobile && (
                  <SKButton
                    onClick={(): void => setIsActive(0)}
                    color="tertiary"
                    iconRight={isDesktop ? Minimize : undefined}
                    size={isDesktop ? ButtonSizes.XL : ButtonSizes.SM}
                  >
                    <span className="text-base xs-only:text-xs text-cloud-900">Minimize</span>
                  </SKButton>
                )}
                <a
                  href={`https://${chainId === 4 ? 'rinkeby.' : ''}etherscan.io/tx/${txnHash ? txnHash : ''}`}
                  target="_blank"
                  rel="noreferrer"
                  className={cls(
                    'sk-cta border border-cloud-900 rounded-xl text-black flex items-center justify-center transition ease-in-out duration-100 bg-kiwi-400 focus:bg-cloud-900 focus:text-white',
                    {
                      'px-2.5 py-1.5 gap-x-1 text-base': isDesktop,
                      'px-1.75 py-1 gap-x-1 text-sm': !isDesktop,
                    },
                  )}
                >
                  <span>Etherscan</span>
                  <IconComp icon={ArrowRight} size={16.67} />
                </a>
              </div>
            </>
          )}
          {swapStatus && (
            <div className="flex w-full">
              <div className="flex items-center w-1/3 border-r border-cloud-900 p-2">
                <SwapStepSvg isUserProposer={isUserProposer} status={swapStatus} />
                <div className="ml-2">
                  {parts[1]?.walletAddress && (
                    <p className="text-sm font-semibold text-cloud-900">
                      {isUserProposer
                        ? 'Swap proposed by you'
                        : `Swap proposed by ${truncateAddress(parts[1]?.walletAddress)}`}
                    </p>
                  )}
                  <p className="text-base xs-only:text-sm text-cloud-900 flex items-center gap-x-0.8125 mt-0.5">
                    {statusDates && statusDates.statusSpan}
                  </p>
                  <p className="text-sm text-cloud-400 mt-0.5">
                    {swapStatus === 'PROPOSED' && parts[1]?.walletAddress && (
                      <span>
                        {isUserProposer ? (
                          <span className="flex items-center gap-x-0.5">
                            Awaiting response from{' '}
                            <span className="font-semibold text-cloud-400">
                              {truncateAddress(parts[1]?.walletAddress)}
                            </span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-x-0.5">
                            <span className="font-semibold text-cloud-400">
                              {truncateAddress(parts[1]?.walletAddress)}
                            </span>
                            is awating your response
                          </span>
                        )}
                      </span>
                    )}
                    {swapStatus === 'INITIATED' && parts[1]?.walletAddress && (
                      <span>
                        {isUserProposer ? (
                          <span className="flex items-center gap-x-0.5">
                            Awaiting for <span className="font-semibold text-cloud-400"> your </span>confirmation
                          </span>
                        ) : (
                          <span className="flex items-center gap-x-0.5">
                            Awaiting for
                            <span className="font-semibold text-cloud-400">
                              {truncateAddress(parts[1]?.walletAddress)}
                            </span>
                            confirmation
                          </span>
                        )}
                      </span>
                    )}
                    {swapStatus === 'EXECUTED' && <span>Swap successful</span>}
                  </p>
                </div>
              </div>
              <div className="w-1/3 border-r border-cloud-900 p-2">
                <p className="text-sm text-cloud-900 font-semibold">Updated:</p>
                <p className="text-base text-cloud-900">
                  {statusDates && statusDates.timestamp.charAt(0).toUpperCase() + statusDates.timestamp.slice(1)}
                </p>
              </div>
              <div className="flex items-center gap-x-1.5 ml-auto p-2">
                {showExpandToggle && isActive === 0 && isBiggerThanMobile && (
                  <SKButton
                    onClick={(): void => setIsActive(2)}
                    color="tertiary"
                    iconRight={Expand}
                    size={ButtonSizes.XL}
                  >
                    <span className="text-base text-cloud-900">Expand</span>
                  </SKButton>
                )}
                {showExpandToggle && isActive > 0 && isBiggerThanMobile && (
                  <SKButton
                    onClick={(): void => setIsActive(0)}
                    color="tertiary"
                    iconRight={Minimize}
                    size={ButtonSizes.XL}
                  >
                    <span className="text-base text-cloud-900">Minimize</span>
                  </SKButton>
                )}
                {swapStatus === 'PROPOSED' && (
                  <SKButton
                    onClick={onShowMoreClick}
                    color="primary"
                    iconRight={ArrowRight}
                    size={isBiggerThanMobile ? ButtonSizes.XL : ButtonSizes.SM}
                  >
                    <span className="text-base xs-only:text-sm text-cloud-900">Show more</span>
                  </SKButton>
                )}
                {swapStatus === 'INITIATED' && (
                  <SKButton
                    onClick={onShowMoreClick}
                    color="primary"
                    iconRight={ArrowRight}
                    size={isBiggerThanMobile ? ButtonSizes.XL : ButtonSizes.SM}
                  >
                    <span className="text-base xs-only:text-sm text-cloud-900">Show more</span>
                  </SKButton>
                )}
                {swapStatus === 'EXECUTED' && txnHash !== undefined && (
                  <SKButton
                    color="primary"
                    iconRight={Link}
                    size={isBiggerThanMobile ? ButtonSizes.XL : ButtonSizes.SM}
                  >
                    <a
                      href={`https://${chainId === 4 ? 'rinkeby.' : ''}etherscan.io/tx/${txnHash ? txnHash : ''}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-base xs-only:text-sm text-cloud-900"
                    >
                      Etherscan
                    </a>
                  </SKButton>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
